import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { api, label, money, timeOf, type Order } from '../lib/api';
import { useStoreEvents } from '../lib/events';
import { useBase, useScope } from '../scope';

const FILTERS: { key: string; label: string; statuses?: string }[] = [
  { key: 'live', label: 'Live', statuses: 'new,accepted,in_progress' },
  { key: 'new', label: 'New', statuses: 'new' },
  { key: 'accepted', label: 'Accepted', statuses: 'accepted' },
  { key: 'in_progress', label: 'In progress', statuses: 'in_progress' },
  { key: 'completed', label: 'Completed', statuses: 'completed' },
  { key: 'closed', label: 'Rejected / cancelled', statuses: 'rejected,cancelled' },
  { key: 'all', label: 'All recent' },
];

const SOURCES: { key: string; label: string }[] = [
  { key: '', label: 'All channels' },
  { key: 'pos', label: 'POS' },
  { key: 'website', label: 'Website' },
  { key: 'uber_eats', label: 'Uber Eats' },
  { key: 'google', label: 'Google' },
];

/** One feed for every channel: the live day sheet and the searchable
 * history are the same surface with a different range. */
export function OrdersFeed() {
  const { rid, sid, store } = useScope();
  const base = useBase();
  const nav = useNavigate();
  const [filter, setFilter] = useState('live');
  const [source, setSource] = useState('');
  const [day, setDay] = useState('');
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // a reload that started under an older filter must not overwrite a newer one
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const next = q.trim()
        ? await api.get<Order[]>(`${base}/orders/search`, { q: q.trim() })
        : await api.get<Order[]>(`${base}/orders`, { status: FILTERS.find((f) => f.key === filter)?.statuses, source: source || undefined, businessDate: day || undefined, limit: 200 });
      if (mine !== seq.current) return;
      setOrders(next);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load.');
    }
  }, [base, filter, source, day, q]);

  const latest = useRef(load);
  latest.current = load;
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 15_000);
    return () => clearInterval(t);
  }, [load]);
  useStoreEvents(base, ['orders'], () => void latest.current());

  async function quick(orderId: string, fn: () => Promise<unknown>) {
    setBusy(orderId);
    try {
      await fn();
      await latest.current();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Store · Orders</div>
          <h1>Orders</h1>
        </div>
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div className="filters" style={{ marginBottom: 10 }}>
        {FILTERS.map((f) => (
          <button key={f.key} className={'filter' + (filter === f.key && !q ? ' on' : '')}
            onClick={() => { setQ(''); setFilter(f.key); }}>
            {f.label}
          </button>
        ))}
        <input className="input" placeholder="Docket, order id, name or phone…" aria-label="Search orders" value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ maxWidth: 260, marginLeft: 'auto' }} />
      </div>
      <div className="filters" style={{ marginBottom: 18 }}>
        {SOURCES.map((s) => (
          <button key={s.key} className={'filter' + (source === s.key ? ' on' : '')} onClick={() => setSource(s.key)}>
            {s.label}
          </button>
        ))}
        <input className="input" type="date" aria-label="Business date" value={day} onChange={(e) => setDay(e.target.value)} style={{ maxWidth: 170, marginLeft: 'auto' }} />
        {day && <button className="btn btn-ghost btn-sm" onClick={() => setDay('')}>Clear date</button>}
      </div>

      {orders === null && <div className="loading">Loading…</div>}
      {orders?.length === 0 && <div className="loading">Nothing here.</div>}
      {orders && orders.length > 0 && (
        <table className="tbl" data-section="orders">
          <thead>
            <tr>
              <th>Docket</th>
              <th>Placed</th>
              <th>Source</th>
              <th>Type</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Payment</th>
              <th className="num">Total</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.orderId} data-order={o.orderId} onClick={() => nav(`/r/${rid}/s/${sid}/orders/${o.orderId}`)}>
                <td className="docket">{o.docketLabel ? `#${o.docketLabel}` : '—'}</td>
                <td className="money">{timeOf(o.placedAt, store?.timezone)}{o.requestedFor ? ` → ${timeOf(o.requestedFor, store?.timezone)}` : ''}</td>
                <td><span className={`src src-${o.source}`}>{label(o.source)}</span></td>
                <td style={{ color: 'var(--ink-2)' }}>{o.collectionType.replace('_', '-')}{o.dineIn ? ` · ${o.dineIn.tableName}` : ''}</td>
                <td>{o.customerName ?? (o.dineIn ? `Table ${o.dineIn.tableName}` : 'Guest')}</td>
                <td><span className={`chip chip-${o.status}`}>{label(o.status)}</span></td>
                <td><span className={`chip chip-${o.paymentState}`}>{label(o.paymentState)}</span></td>
                <td className="money num" style={{ fontWeight: 700 }}>{money(o.grandTotalInCents)}</td>
                <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                  {o.status === 'new' && (
                    <>
                      <button className="btn btn-primary btn-sm" disabled={busy === o.orderId}
                        onClick={() => void quick(o.orderId, () => api.post(`${base}/orders/${o.orderId}/accept`, { etaMinutes: 15 }))}>
                        Accept
                      </button>{' '}
                      <button className="btn btn-danger btn-sm" disabled={busy === o.orderId}
                        onClick={() => {
                          const reason = prompt('Reason for rejecting?');
                          if (reason) void quick(o.orderId, () => api.post(`${base}/orders/${o.orderId}/reject`, { reason }));
                        }}>
                        Reject
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
